import React, { useMemo, useCallback, useEffect, useRef } from "react";import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  LineSeries,
  ColumnSeries,
  Category,
  Tooltip,
  Inject,
  AreaSeries,
  Zoom,
  Crosshair,
} from "@syncfusion/ej2-react-charts";
import { formatNumber } from "../../utils/formatNumber";
import {
  MINUTE_ZOOM_SETTINGS,
  NO_CROSSHAIR,
  NO_ZOOM_SETTINGS,
  minuteCrosshair,
  minuteLabel,
  minuteRangeOf,
  useMinuteAxisFor,
} from "./minuteAxis";

import "./Chart.css";
import "./ImpressionsChart.css";

/* =========================
   TIME HELPERS
========================= */

function minuteIndexToTimeLabel(index) {
  const i = Number(index);
  if (!Number.isFinite(i)) return "";
  const hh = String(Math.floor(i / 60)).padStart(2, "0");
  const mm = String(i % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

/**
 * IMPORTANT:
 * - We support BOTH "minute-index" mode (x is 0..1439) AND
 *   "category minute" mode (x is "HH:mm") without breaking other charts.
 * - If series has "label" like "HH:mm" in minute mode, we will use Category axis.
 */
function isHHmmLabel(v) {
  return typeof v === "string" && /^\d{2}:\d{2}$/.test(v);
}

function hhmmToMinuteIndex(hhmm) {
  if (!isHHmmLabel(hhmm)) return null;
  const [hh, mm] = hhmm.split(":").map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return hh * 60 + mm;
}

function timeToMinuteIndex(t) {
  if (!t) return null;
  const [hh, mm] = String(t).split(":").map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return hh * 60 + mm;
}

/* =========================
   COMPONENT
========================= */

export const ImpressionsChart = ({
  series = [],
  mode = "minute",
  chartType = "line",
  startTime,
  endTime,
  refreshKey,
  title = "Switch-In",
  showYesterday = false,
}) => {
  const chartRef = useRef(null);
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.refresh();
  }, [refreshKey]);
  const isMinute = mode === "minute";
  const isHour = mode === "hour";
    const storedUser = JSON.parse(
    localStorage.getItem("adm_panel_user") || "{}"
  );

  const isDark = !!storedUser.darkmode;

  const axisTextColor = isDark ? "#ffffff" : "#666666";
  const tickColor = isDark ? "#64748b" : "#9ca3af";

  // The category axis can only label whole hours, so a series carrying minute
  // indexes takes the numeric path instead.
  const hasMinuteIndex = useMemo(
    () => minuteRangeOf(series, mode) !== null,
    [series, mode]
  );

  const minuteUsesCategory = useMemo(() => {
    if (!isMinute || hasMinuteIndex) return false;
    const sample = series?.[0];
    if (sample && isHHmmLabel(sample.label)) return true;
    if (sample && isHHmmLabel(sample.x)) return true;
    return false;
  }, [isMinute, hasMinuteIndex, series]);

  /* =========================
     Minute range (from selectors)
  ========================= */
  const minuteRange = useMemo(() => {
    if (!isMinute) return null;

    const min = timeToMinuteIndex(startTime) ?? 0;
    const max = timeToMinuteIndex(endTime) ?? 1439;

    return {
      min: Math.max(0, min),
      max: Math.min(1439, max),
    };
  }, [isMinute, startTime, endTime]);

  // Range comes from the time selectors, not the series.
  const {
    ref: wrapRef,
    onZoomComplete: handleZoomComplete,
    axisProps: minuteAxisProps,
  } = useMinuteAxisFor(minuteUsesCategory ? null : minuteRange, tickColor);

  /* =========================
     NORMALIZE DATA
     - minute:
        a) Category minute ("HH:mm") -> x is "HH:mm"
        b) Double minute (0..1439)   -> x is number
     - non-minute: Category with "__idx" encoding
  ========================= */
  const { chartData, yAxisMax } = useMemo(() => {
    if (!Array.isArray(series) || series.length === 0) {
      return { chartData: [], yAxisMax: 10 };
    }

    let data = series.map((p, i) => {
      if (isMinute) {
        // Case A: Category minute series (preferred, trims naturally)
        if (minuteUsesCategory) {
          const lbl =
            isHHmmLabel(p.label) ? p.label : isHHmmLabel(p.x) ? p.x : "";
          return { x: lbl, y: Number(p.value) || 0, _i: i };
        }

        // Case B: Double minute series (0..1439)
        const idx = Number.isFinite(Number(p.index))
          ? Number(p.index)
          : Number.isFinite(Number(p.x))
          ? Number(p.x)
          : Number(p.label);

        return { x: idx, y: Number(p.value) || 0, _i: i };
      }

      // non-minute: encode stable index for sparse label rendering
      const label = String(p.label ?? p.x ?? "");
      return { x: `${label}__${i}`, y: Number(p.value) || 0, _i: i };
    });

    // Extra safety:
    // If minute is category-based but series still includes out-of-range labels,
    // trim by converting label -> minuteIndex.
    if (isMinute && minuteUsesCategory && minuteRange) {
      data = data.filter((d) => {
        const mi = hhmmToMinuteIndex(d.x);
        if (mi == null) return false;
        return mi >= minuteRange.min && mi <= minuteRange.max;
      });
    }

    // If minute is double-based, we DO NOT trim here unless your series is still full-day.
    // You already trim in the hook; trimming here can cause empty axis side-effects.
    // However, if your hook returns full-day in minute mode, this will still respect time.
    if (isMinute && !minuteUsesCategory && minuteRange) {
      data = data.filter((d) => d.x >= minuteRange.min && d.x <= minuteRange.max);
    }

    const max = Math.max(...data.map((d) => d.y), 0);
    return {
      chartData: data,
      yAxisMax: max > 0 ? Math.ceil(max * 1.15) : 10,
    };
  }, [series, isMinute, minuteUsesCategory, minuteRange]);

  /* =========================
     CATEGORY DECODE (non-minute)
  ========================= */
  const decode = useCallback((v) => {
    const raw = String(v ?? "");
    const [label, idx] = raw.split("__");
    return { label, idx: Number(idx) };
  }, []);

  /* =========================
     ADAPTIVE HOUR STEP
     ≤ 7 days → 6h
     > 7 days → 8h
  ========================= */
  const hourLabelStep = useMemo(() => {
    if (!isHour || series.length === 0) return 1;

    const days = new Set(
      series
        .map((p) => String(p.label || "").split(" ")[0])
        .filter(Boolean)
    );

    // For overview (single day), show every hour
    if (days.size === 1) return 1;

    return days.size <= 7 ? 6 : 8;
  }, [isHour, series]);

  /* =========================
     AXIS LABELS
  ========================= */
  const handleAxisLabelRender = useCallback(
    (args) => {
      // Format Y-axis numbers with thousand separators
      if (args.axis?.name === "primaryYAxis") {
        const value = parseFloat(args.value);
        if (!isNaN(value)) {
          args.text = formatNumber(value);
        }
        return;
      }

      if (args.axis?.name !== "primaryXAxis") return;

      /* ---- MINUTE ---- */
      if (isMinute) {
        // Category minute: axis already uses "HH:mm"
        if (minuteUsesCategory) {
          const t = String(args.text ?? "");
          const mi = hhmmToMinuteIndex(t);
          if (mi == null) {
            args.text = "";
            return;
          }

          // show only full hours + final selected minute
          if (
            mi % 60 !== 0 &&
            mi !== minuteRange?.max
          ) {
            args.text = "";
            return;
          }

          // keep as HH:mm
          args.text = t;
          return;
        }

        // Every tick the axis emits is already a clock minute.
        const index = Number(args.value);
        if (!Number.isFinite(index)) {
          args.text = "";
          return;
        }

        args.text = minuteIndexToTimeLabel(index);
        return;
      }

      /* ---- NON-MINUTE CATEGORY ---- */
      const { label, idx } = decode(args.text);
      if (!Number.isFinite(idx)) {
        args.text = "";
        return;
      }

      if (isHour) {
        // Show labels at intervals based on hourLabelStep
        if (idx % hourLabelStep !== 0) {
          args.text = "";
          return;
        }
        args.text = label; // "DD.MM HH:00"
        return;
      }

      // day/week/month: show label as-is
      args.text = label;
    },
    [isMinute, minuteUsesCategory, minuteRange, decode, isHour, hourLabelStep]
  );

  /* =========================
     TOOLTIP
  ========================= */
  const handleTooltipRender = useCallback(
    (args) => {
      if (!args.point) return;

      if (isMinute) {
        if (minuteUsesCategory) {
          // point.x is "HH:mm"
          args.text = `${args.point.x} : ${formatNumber(args.point.y)}`;
          return;
        }

        // point.x is minute index
        args.text = `${minuteIndexToTimeLabel(args.point.x)} : ${formatNumber(args.point.y)}`;
        return;
      }

      const raw = String(args.point.x ?? "");
      const label = raw.includes("__") ? raw.split("__")[0] : raw;
      args.text = `${label} : ${formatNumber(args.point.y)}`;
    },
    [isMinute, minuteUsesCategory]
  );

  const handleSharedTooltipRender = useCallback((args) => {
    const point = args.point?.[0];
    if (!point) return;
    args.headerText = minuteLabel(Number(point.x));
    args.text = [formatNumber(point.y)];
  }, []);

  // Syncfusion throws if zoomSettings changes as a property, so the axis kind
  // is part of the key and remounts instead.
  const chartKey = `impressions-${mode}-${chartType}-${minuteUsesCategory ? "cat" : "dbl"}-${
    minuteAxisProps ? "clock" : "plain"
  }`;

  return (
    <div className="chart-container impressions-chart" ref={wrapRef}>
      <h3 className="chart-title">{title}</h3>
      {showYesterday && (
        <p style={{ marginTop: "-8px", marginBottom: "12px", fontSize: "14px", color: "#666" }}>Yesterday</p>
      )}

      <ChartComponent
        ref={chartRef}
        key={chartKey}
        height="420px"
        margin={{ left: 15, right: 40 }}
        primaryXAxis={{
          name: "primaryXAxis",
          valueType: isMinute
            ? (minuteUsesCategory ? "Category" : "Double")
            : "Category",
          interval: isMinute && !minuteUsesCategory ? 60 : 1,
          minimum: isMinute && !minuteUsesCategory ? minuteRange?.min : undefined,
          maximum: isMinute && !minuteUsesCategory ? minuteRange?.max : undefined,

          rangePadding: chartType === "bar" ? "Additional" : "None",
          labelPlacement: "OnTicks",
          labelIntersectAction: "Rotate45",
          majorGridLines: { width: 0 },
          majorTickLines: { width: 0 },
          ...(minuteAxisProps || {}),
          labelStyle: {
            fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
            color: axisTextColor
          },
        }}
        primaryYAxis={{
          minimum: 0,
          maximum: yAxisMax,
          rangePadding: "None",
          majorTickLines: { width: 0 },
          lineStyle: { width: 0 },
          labelFormat: "n0",
          labelStyle: {
            fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
            color: axisTextColor
          },
        }}
        tooltip={{ enable: true, shared: !!minuteAxisProps }}
        crosshair={minuteAxisProps ? minuteCrosshair(tickColor) : NO_CROSSHAIR}
        zoomSettings={minuteAxisProps ? MINUTE_ZOOM_SETTINGS : NO_ZOOM_SETTINGS}
        zoomComplete={handleZoomComplete}
        tooltipRender={handleTooltipRender}
        sharedTooltipRender={handleSharedTooltipRender}
        axisLabelRender={handleAxisLabelRender}
      >
        <Inject services={[LineSeries, ColumnSeries, AreaSeries, Category, Tooltip, Zoom, Crosshair]} />

        <SeriesCollectionDirective>
          {/* Area fill stays for all modes */}
          <SeriesDirective
            dataSource={chartData}
            xName="x"
            yName="y"
            type="Area"
            fill="#1976d2"
            opacity={0.12}
            border={{ width: 0 }}
            animation={{ enable: true, duration: 400 }}
            // Otherwise the fill adds a duplicate shared-tooltip row.
            enableTooltip={false}
          />

        <SeriesDirective
          dataSource={chartData}
          xName="x"
          yName="y"
          type={chartType === "bar" ? "Column" : "Line"}
          width={2}
          fill="#1976d2"
          border={{ color: "#1976d2", width: 2 }}
          animation={{ enable: true, duration: 400 }}
        />
        </SeriesCollectionDirective>
      </ChartComponent>
    </div>
  );
};

export default ImpressionsChart;
